import { type FC, useState, useCallback, useEffect } from "react";
import {
  AsyncSearchableSelectBox,
  type Option,
  type GroupedOptions,
} from "@root/shared/components/AsyncSearchableSelectBox";
import { components, type MenuProps, type OptionProps } from "react-select";
import * as selectBoxStyles from "@root/shared/components/AsyncSearchableSelectBox/styles.css";
import * as styles from "./styles.css";
import type { ClientShop, Work, Props } from "./types";

// 検索結果の上限を示すダミーオプションのvalue
const MORE_RESULTS_INDICATOR_VALUE = -1;
const MORE_RESULTS_MESSAGE = "20件まで表示中：キーワード入力でさらに絞り込みが可能です。";

export const JobEntrySearchSelector: FC<Props> = ({
  selectedShopId,
  selectedWorkId,
  selectedShopName,
  selectedWorkTitle,
  searchClientShopsUrl,
  searchWorksUrl,
  shopPlaceholder,
  workPlaceholder,
  shopFieldName,
  workFieldName,
  isSp,
}) => {
  // 初期値の設定: IDと名前の両方がある場合のみOptionを作成
  const initialShop =
    selectedShopId && selectedShopName
      ? { value: Number(selectedShopId), label: selectedShopName }
      : undefined;
  const initialWork =
    selectedWorkId && selectedWorkTitle
      ? { value: Number(selectedWorkId), label: selectedWorkTitle }
      : undefined;

  // 店舗と求人の選択されたOptionを管理
  const [selectedShop, setSelectedShop] = useState<Option<number> | undefined>(initialShop);
  const [selectedWork, setSelectedWork] = useState<Option<number> | undefined>(initialWork);

  // 店舗検索のloadOptions関数
  const loadShopOptions = useCallback(
    async (inputValue: string): Promise<Option<number>[]> => {
      const params = new URLSearchParams();
      if (inputValue.trim()) params.append("query", inputValue);
      if (selectedWork) params.append("work_id", selectedWork.value.toString());

      const response = await fetch(`${searchClientShopsUrl}?${params}`);
      const result = (await response.json()) as { data: ClientShop[]; truncated: boolean };

      const options: Option<number>[] = (result.data || []).map((shop) => ({
        value: shop.id,
        label: shop.name,
      }));

      // 20件超の場合、末尾に案内メッセージを追加
      if (result.truncated) {
        options.push({
          value: MORE_RESULTS_INDICATOR_VALUE,
          label: MORE_RESULTS_MESSAGE,
        });
      }

      return options;
    },
    [searchClientShopsUrl, selectedWork],
  );

  // 求人検索のloadOptions関数
  const loadWorkOptions = useCallback(
    async (inputValue: string): Promise<Option<number>[]> => {
      const params = new URLSearchParams();
      if (inputValue.trim()) params.append("query", inputValue);
      if (selectedShop) params.append("client_shop_id", selectedShop.value.toString());

      const response = await fetch(`${searchWorksUrl}?${params}`);
      const result = (await response.json()) as { data: Work[]; truncated: boolean };

      const options: Option<number>[] = (result.data || []).map((work) => ({
        value: work.id,
        label: work.title,
      }));

      // 20件超の場合、末尾に案内メッセージを追加
      if (result.truncated) {
        options.push({
          value: MORE_RESULTS_INDICATOR_VALUE,
          label: MORE_RESULTS_MESSAGE,
        });
      }

      return options;
    },
    [searchWorksUrl, selectedShop],
  );

  // 店舗選択時の処理（案内メッセージは選択不可）
  const handleShopChange = useCallback((shop: Option<number> | undefined) => {
    if (shop?.value === MORE_RESULTS_INDICATOR_VALUE) return;
    setSelectedShop(shop);
    setSelectedWork(undefined);
  }, []);

  // 求人選択時の処理（案内メッセージは選択不可）
  const handleWorkChange = useCallback((work: Option<number> | undefined) => {
    if (work?.value === MORE_RESULTS_INDICATOR_VALUE) return;
    setSelectedWork(work);
  }, []);

  // app/assets/javascripts/client_frontend/sp/job_entries.js:63,
  // app/assets/javascripts/client_frontend/pc/job_entries.js.erb:675 の
  // キャンセルボタン押下時のカスタムイベントをここで受け取る
  // TODO: 応募検索フィルターをReactに統合してこのカスタムイベントを削除したい
  useEffect(() => {
    const handleClear = () => {
      setSelectedShop(undefined);
      setSelectedWork(undefined);
    };

    window.addEventListener("jobEntrySearchSelectorClear", handleClear);

    return () => {
      window.removeEventListener("jobEntrySearchSelectorClear", handleClear);
    };
  }, []);

  const debounceMs = 300;
  // SP画面の場合のみ、UIkitモーダル内でメニュークリック時にモーダルが閉じるのを防ぐ
  const customMenu = isSp ? SpCustomMenu : undefined;

  return (
    <div className={styles.jobEntrySearchSelector}>
      <input
        type="hidden"
        id="client_shop_id"
        name={shopFieldName}
        value={selectedShop?.value ?? ""}
      />
      <input type="hidden" id="work_id" name={workFieldName} value={selectedWork?.value ?? ""} />
      <div className={styles.searchField}>
        <AsyncSearchableSelectBox
          value={selectedShop}
          onChange={handleShopChange}
          loadOptions={loadShopOptions}
          placeholder={shopPlaceholder}
          noOptionsMessage="該当する店舗が見つかりません"
          instanceId="shop-search"
          customOption={CustomOption}
          customMenu={customMenu}
          debounceMs={debounceMs}
          defaultOptions
        />
      </div>

      <div className={styles.searchField}>
        {/* 店舗変更時にkeyが変わり、コンポーネントが再マウントされて求人の選択肢が再ロードされる */}
        <AsyncSearchableSelectBox
          key={selectedShop?.value ?? "no-shop"}
          value={selectedWork}
          onChange={handleWorkChange}
          loadOptions={loadWorkOptions}
          placeholder={workPlaceholder}
          noOptionsMessage="該当する求人が見つかりません"
          instanceId="work-search"
          customOption={CustomOption}
          customMenu={customMenu}
          debounceMs={debounceMs}
          defaultOptions
        />
      </div>
    </div>
  );
};

// SP画面用のカスタムメニュー: UIkitモーダル内でメニュークリック時にモーダルが閉じるのを防ぐ
const SpCustomMenu = <T extends Option<number>>(props: MenuProps<T, false, GroupedOptions<T>>) => (
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
  <div
    onClick={(e) => {
      // イベントの伝播を止めることで、UIkitモーダルが閉じるのを防ぐ
      e.stopPropagation();
    }}
  >
    <components.Menu {...props} />
  </div>
);

// カスタムOption: 案内メッセージの場合は特別なスタイルを適用
const CustomOption = <T extends Option<number>>(
  props: OptionProps<T, false, GroupedOptions<T>>,
) => {
  const { data } = props;

  // 案内メッセージの場合
  if (data.value === MORE_RESULTS_INDICATOR_VALUE) {
    return <div className={styles.moreResultsIndicator}>{data.label}</div>;
  }

  // 通常のオプション（デフォルトと同じスタイルを適用）
  return (
    <components.Option {...props}>
      <div className={selectBoxStyles.optionInner}>
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};
